import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Pill } from "@phosphor-icons/react"
import { HighlightedText } from "@/components/highlighted-text"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { joinStrings } from "@/lib/joinString"
import { chMedicationFragmentInline$data } from "@/modules/patients/detail/medication/ch/services/__generated__/chMedicationFragmentInline.graphql"
import { chMedicationRelationsByDiagnosisCodeQuery$data } from "@/modules/patients/detail/medication/ch/services/__generated__/chMedicationRelationsByDiagnosisCodeQuery.graphql"
import {
  ChMedicationSearchState,
  MedicationDetail,
  MedicationHighlight,
  MedicationItem,
} from "@/modules/patients/detail/medication/components/ch-medication-search"
import { FreeTextMedicationCreation } from "@/modules/patients/detail/medication/components/free-text-medication-creation"

type HighlightAction = {
  handleHighlightContent: (
    defaultValues: { name: string; mainSubstance?: string | null },
    highlights?: MedicationHighlight,
  ) => { title: string; isHighlight: boolean }
}

type ChMedicationSearchResultWithTemplateProps = ChMedicationSearchState &
  HighlightAction & {
    existingMedications?: { title: string; options: chMedicationFragmentInline$data[] }[]
    searchResultsRef: React.RefObject<HTMLDivElement | null>
    searchValue: string
    useMedicationIcon?: boolean
    handleSelectFreeTextSearch: VoidFunction
    handleSelectMedication: (item: Partial<MedicationItem>) => void
    handleSelectExistingMedication: (item: chMedicationFragmentInline$data, medication?: MedicationDetail) => void
    medicationRelationsByDiagnosisCode?: chMedicationRelationsByDiagnosisCodeQuery$data["ch_medicationRelationsByDiagnosisCode"]
  }

type ChMedicationSearchResultWithTemplateItemProps = HighlightAction & {
  information?: {
    readonly dispensingCategory: string | null
    readonly formattedPrice: string | null
    readonly mainSubstance: string | null
    readonly packageSizeDescription: string | null
    readonly refundCategory: string | null
  }
  useMedicationIcon?: boolean
  searchValue: string
  name: string
  highlights?: MedicationHighlight
  category?: string | null
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
}

const ChMedicationSearchResultWithTemplateItem = (props: ChMedicationSearchResultWithTemplateItemProps) => {
  const { onClick, handleHighlightContent, category, information, highlights, name, searchValue, useMedicationIcon } =
    props

  const informationText = information
    ? joinStrings(
        [
          information.packageSizeDescription,
          information.formattedPrice,
          information.dispensingCategory,
          information.refundCategory,
        ].filter((f) => f),
      )
    : category
  const { title, isHighlight } = handleHighlightContent({ mainSubstance: information?.mainSubstance, name }, highlights)

  return (
    <div
      className="aria-selected:bg-accent aria-selected:text-accent-foreground hover:bg-surface-hovered relative cursor-pointer items-center rounded-xl p-0 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50"
      role="option"
      data-testid="medication-search-item"
      onClick={onClick}>
      <div className="group disabled:text-on-surface-disabled flex w-full min-w-0 items-center overflow-hidden rounded-md px-2 py-1.5 text-left">
        {useMedicationIcon && <Pill size={24} className="mr-3" />}
        <div className="flex-col">
          <p className="text-on-surface group-disabled:text-on-surface-disabled min-w-0 truncate text-sm leading-5">
            {isHighlight ? (
              <PotentiallyUnsafeInnerHtml value={title} />
            ) : (
              <HighlightedText searchValue={searchValue} text={title} />
            )}
          </p>
          <span className="text-on-surface-subdued text-xs">{informationText}</span>
        </div>
      </div>
    </div>
  )
}

export const ChMedicationSearchResultWithTemplate = (props: ChMedicationSearchResultWithTemplateProps) => {
  const {
    existingMedications = [],
    searchValue,
    useMedicationIcon,
    data,
    searchResultsRef,
    searchResultStatus,
    handleSelectFreeTextSearch,
    handleSelectMedication,
    handleSelectExistingMedication,
    handleHighlightContent,
    medicationRelationsByDiagnosisCode,
  } = props
  const { t } = useTranslation()
  const filterOptionsBySearchTerm = useCallback(
    (label: string) => {
      if (!data) return true
      return label.toLowerCase().includes(searchValue.toLowerCase()) || data.some((template) => template.name === label)
    },
    [data, searchValue],
  )

  const chMedicationRelationsByDiagnosisCodeFilter = medicationRelationsByDiagnosisCode
    ? medicationRelationsByDiagnosisCode.filter((item) => {
        if (existingMedications.length === 0) return item

        return (
          filterOptionsBySearchTerm(item.medicationName) &&
          // the first existingMedications is active medications, the second is inactive medications
          !existingMedications[0].options.find((med) => med.pharmaCode === item.pharmacode)
        )
      })
    : []

  const filteredExistingMedications = useMemo(() => {
    const filteredExistingMedications: { title: string; options: chMedicationFragmentInline$data[] }[] = []
    existingMedications.forEach((group) => {
      const options = group.options.filter((i) => filterOptionsBySearchTerm(i.name))
      if (options.length > 0) {
        filteredExistingMedications.push({ title: group.title, options })
      }
    })
    return filteredExistingMedications
  }, [existingMedications, filterOptionsBySearchTerm])

  const { nonTemplateMedications, templateMedications } = useMemo(() => {
    const templateMedications = data?.filter((item) => item.medicationType === "TEMPLATE" && item.pharmacode) ?? []
    const nonTemplateMedications = data?.filter((item) => item.medicationType !== "TEMPLATE") ?? []
    return { templateMedications, nonTemplateMedications }
  }, [data])

  return (
    <ScrollWrapper ref={searchResultsRef} testId="medication-search-results">
      {searchResultStatus === "noResults" ? (
        <FreeTextMedicationCreation
          className="mt-4 px-2"
          searchTerm={searchValue}
          onClick={handleSelectFreeTextSearch}
        />
      ) : (
        <>
          {chMedicationRelationsByDiagnosisCodeFilter.length > 0 && (
            <>
              <div className="text-on-surface-subdued overflow-hidden p-2 text-sm font-medium">
                {t("patient.healthInformation.medication.diagnosisBasedSuggestions")}
              </div>
              {chMedicationRelationsByDiagnosisCodeFilter.map((item) => (
                <ChMedicationSearchResultWithTemplateItem
                  key={item.pharmacode}
                  name={item.medicationName}
                  searchValue={searchValue}
                  useMedicationIcon={useMedicationIcon}
                  onClick={() => {
                    handleSelectMedication({
                      itemId: item.medicationTemplateId ?? undefined,
                      medicationType: item.medicationTemplateId ? "TEMPLATE" : "PREPARATION",
                      name: item.medicationName,
                      pharmacode: item.pharmacode,
                    })
                  }}
                  handleHighlightContent={handleHighlightContent}
                />
              ))}
            </>
          )}
          {filteredExistingMedications.map((group, index) => {
            if (group.options.length === 0) return null
            return (
              <div key={group.title} role="group" data-testid={`${t(group.title).toLowerCase()}-medication-section`}>
                {index !== 0 && filteredExistingMedications[index - 1]?.options.length > 0 && (
                  <div className="my-2.5 border-t" />
                )}
                <div className="text-on-surface-subdued overflow-hidden p-2 text-sm font-medium">{t(group.title)}</div>
                {group.options.map((item) => (
                  <ChMedicationSearchResultWithTemplateItem
                    key={item.id}
                    name={item.name}
                    searchValue={searchValue}
                    useMedicationIcon={useMedicationIcon}
                    onClick={() => handleSelectExistingMedication(item)}
                    handleHighlightContent={handleHighlightContent}
                  />
                ))}
              </div>
            )
          })}
          {templateMedications.length > 0 && (
            <div role="group">
              {filteredExistingMedications.length > 0 && <div className="my-2.5 border-t" />}
              <div className="text-on-surface-subdued overflow-hidden p-2 text-sm font-medium">
                {t("patient.medication.withTemplates")}
              </div>
              {templateMedications.map((item) => (
                <ChMedicationSearchResultWithTemplateItem
                  key={item.id}
                  name={item.name}
                  searchValue={searchValue}
                  highlights={item.highlights}
                  useMedicationIcon={useMedicationIcon}
                  category={item.category}
                  information={{
                    dispensingCategory: item.dispensingCategory,
                    formattedPrice: item.formattedPrice,
                    mainSubstance: item.mainSubstance,
                    packageSizeDescription: item.packageSizeDescription,
                    refundCategory: item.refundCategory,
                  }}
                  onClick={() => handleSelectMedication(item)}
                  handleHighlightContent={handleHighlightContent}
                />
              ))}
            </div>
          )}
          {nonTemplateMedications.length > 0 && (
            <div role="group">
              <div className="my-2.5 border-t" />
              <div className="text-on-surface-subdued overflow-hidden p-2 text-sm font-medium">
                {t("patient.medication.allProductsAndSubstances")}
              </div>
              {nonTemplateMedications.map((item) => (
                <ChMedicationSearchResultWithTemplateItem
                  key={item.id}
                  name={item.name}
                  searchValue={searchValue}
                  highlights={item.highlights}
                  useMedicationIcon={useMedicationIcon}
                  category={item.category}
                  information={{
                    dispensingCategory: item.dispensingCategory,
                    formattedPrice: item.formattedPrice,
                    mainSubstance: item.mainSubstance,
                    packageSizeDescription: item.packageSizeDescription,
                    refundCategory: item.refundCategory,
                  }}
                  onClick={() => handleSelectMedication(item)}
                  handleHighlightContent={handleHighlightContent}
                />
              ))}
            </div>
          )}
        </>
      )}
    </ScrollWrapper>
  )
}
